import CryptoJS from "crypto-js";
import axios from "axios";
import { assertPayloadWithinLimits, assertQueryWithinLimit } from "./requestLimits";
import { sanitizePayload, sanitizeQueryString } from "./inputSanitizer";
import apiConfig from "../config/configRoutes";

const REQUEST_CRYPTO_KEY = "EcoClubsForMissionLiFEAICTEADMIN1@1234SecretKey";

// Only our own backend understands {enc} bodies and ?enc= queries. Other hosts
// whose paths also contain /api/ (the Anuvadini translation / read-aloud
// services) must get plain JSON — encrypting them made every translation call
// fail with 400. The prod origin is listed because the RTK baseUrl is
// hardcoded to it even in local builds.
const OWN_API_ORIGINS = new Set(
  [apiConfig.baseURL, "https://ecoclubs.education.gov.in"]
    .map((u) => {
      try {
        return new URL(u).origin;
      } catch {
        return null;
      }
    })
    .filter(Boolean)
);

const isApiUrl = (url = "") => {
  if (typeof url !== "string" || !url) return false;
  try {
    const here = typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
    const parsed = new URL(url, here);
    if (parsed.origin !== here && !OWN_API_ORIGINS.has(parsed.origin)) return false;
    // includes(), not startsWith(): some callers build "…gov.in//api/…"
    return parsed.pathname.includes("/api/") || parsed.pathname === "/api";
  } catch {
    return false;
  }
};

const encryptPayload = (payload) => {
  const raw = JSON.stringify(payload ?? {});
  return CryptoJS.AES.encrypt(raw, REQUEST_CRYPTO_KEY).toString();
};

const shouldEncryptBody = (data, headers = {}) => {
  if (!data || typeof data !== "object") return false;
  const contentType = String(headers["Content-Type"] || headers["content-type"] || "").toLowerCase();
  if (typeof FormData !== "undefined" && data instanceof FormData) return false;
  if (contentType.includes("multipart/form-data")) return false;
  return true;
};

const encryptUrlQuery = (url) => {
  try {
    const origin = typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
    const parsed = new URL(url, origin);
    if (!parsed.search || parsed.searchParams.size === 0) return url;
    const queryObj = {};
    parsed.searchParams.forEach((value, key) => {
      queryObj[key] = value;
    });
    parsed.search = "";
    parsed.searchParams.set("enc", encryptPayload(queryObj));
    return parsed.toString().replace(origin, "");
  } catch {
    return url;
  }
};

const normalizeHeaders = (headers) => {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const out = {};
    headers.forEach((v, k) => {
      out[k] = v;
    });
    return out;
  }
  return { ...headers };
};

export const secureApiRequest = (method, url, data, headers) => {
  // CRITICAL: FormData must pass through unchanged for file uploads
  if (data instanceof FormData) {
    console.log('🔓 Skipping encryption for FormData - file upload detected');
    return { url, data, headers: headers || {} };
  }

  let finalUrl = sanitizeQueryString(url || "");
  let finalData = sanitizePayload(data);
  const finalHeaders = headers ? { ...headers } : {};
  const verb = String(method || "GET").toUpperCase();

  if (!isApiUrl(url)) {
    return { url: finalUrl, data: finalData, headers: finalHeaders };
  }

  if (["GET", "HEAD"].includes(verb)) {
    assertQueryWithinLimit(finalUrl);
    finalUrl = encryptUrlQuery(finalUrl);
    return { url: finalUrl, data: finalData, headers: finalHeaders };
  }

  assertPayloadWithinLimits(data);

  if (shouldEncryptBody(data, finalHeaders)) {
    finalData = { enc: encryptPayload(data) };
    if (!finalHeaders["Content-Type"] && !finalHeaders["content-type"]) {
      finalHeaders["Content-Type"] = "application/json";
    }
  }

  return { url: finalUrl, data: finalData, headers: finalHeaders };
};

export const initializeGlobalTransportEncryption = () => {
  const browserWindow = typeof window !== "undefined" ? window : null;
  if (!browserWindow || typeof browserWindow.fetch !== "function") return;

  const originalFetch = browserWindow.fetch.bind(browserWindow);
  browserWindow.fetch = async (input, init = {}) => {
    const isRequestObject = typeof input !== "string" && input instanceof Request;
    const reqUrl = typeof input === "string" ? input : input?.url || "";
    const inputMethod = typeof input === "string" ? undefined : input?.method;
    const method = (init.method || inputMethod || "GET").toUpperCase();
    const inputHeaders = typeof input === "string" ? {} : normalizeHeaders(input?.headers);
    const headers = { ...inputHeaders, ...normalizeHeaders(init.headers) };
    let body = init.body;
    if (
      body === undefined &&
      typeof input !== "string" &&
      input instanceof Request &&
      !["GET", "HEAD"].includes(method)
    ) {
      body = await input.clone().text();
    }
    let finalUrl = reqUrl;
    let finalHeaders = headers;
    const requestDerivedInit = isRequestObject
      ? {
          credentials: input.credentials,
          cache: input.cache,
          mode: input.mode,
          redirect: input.redirect,
          referrer: input.referrer,
          referrerPolicy: input.referrerPolicy,
          integrity: input.integrity,
          keepalive: input.keepalive,
          signal: input.signal,
        }
      : {};

    if (isApiUrl(reqUrl)) {
      if (["GET", "HEAD"].includes(method)) {
        const secured = secureApiRequest(method, reqUrl, undefined, headers);
        finalUrl = secured.url;
      } else if (body && typeof body === "string" && String(headers["Content-Type"] || headers["content-type"] || "").includes("application/json")) {
        try {
          const parsed = JSON.parse(body);
          const secured = secureApiRequest(method, reqUrl, parsed, headers);
          finalUrl = secured.url;
          finalHeaders = secured.headers;
          body = JSON.stringify(secured.data);
        } catch {
          // keep original body if not valid JSON
        }
      }
    }

    return originalFetch(finalUrl, {
      ...requestDerivedInit,
      ...init,
      method,
      headers: finalHeaders,
      body,
    });
  };

  axios.interceptors.request.use((config) => {
    const method = String(config.method || "GET").toUpperCase();
    const url = config.url || "";
    const secured = secureApiRequest(method, url, config.data, config.headers || {});
    return { ...config, url: secured.url, data: secured.data, headers: secured.headers };
  });
};

